import { FilterInput } from '@apeiron/common'
import { NextImage, useAccountContext } from '@apeiron/temp'
import { ApeironTooltipInfoButton } from '@apeiron/ui'
import { Box, ButtonBase, styled } from '@mui/material'
import AddSVG from '@public/icons/icon-add.svg'
import {
  FilterSection,
  FilterSectionContent,
  FilterSectionHeader,
  FilterSectionTitle
} from '@src/components/share/filter/FilterSection'
import SaveFilterDialog from '@src/components/share/filter/saveFilter/SaveFilterDialog'
import SaveFilterItem from '@src/components/share/filter/saveFilter/SaveFilterItem'
import { FavoriteType } from '@src/constants/favoriteFilter'
import useCreateFavoriteFilter from '@src/hooks/graphql/useCreateFavoriteFilter'
import useGetFavoriteFilter from '@src/hooks/graphql/useGetFavoriteFilters'
import useRemoveFavoriteFilter from '@src/hooks/graphql/useRemoveFavoriteFilter'
import {
  FavoriteFilter,
  SortInputFromFilterInput
} from '@src/types/favoriteFilter'
import equals from 'fast-deep-equal'
import * as R from 'ramda'
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const Header = styled(FilterSectionHeader)`
  &.MuiAccordionSummary-root {
    margin-bottom: 4px;
  }
`

const Content = styled(FilterSectionContent)`
  &.MuiAccordionDetails-root {
    padding: 0px;
  }
`

const AddButton = ButtonBase

const ItemList = styled('ul')`
  list-style: none;
  margin: 0;
  padding: 4px;
`

const Item = SaveFilterItem

const Icon = styled(NextImage)`
  filter: invert(78%);
  &:hover {
    filter: invert(86%);
  }
  &:disabled {
    filter: invert(6%);
  }
  height: 21px;
  aspect-ratio: 1;
`

const WithInfoContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 10px;
`

const Info = styled(ApeironTooltipInfoButton)`
  .Custom-Button {
    width: 14px;
    background-color: transparent;
  }
`

const SaveFilter = <T extends FilterInput>(props: SaveFilterProps<T>) => {
  const { className, filterInput, sortInput, type, onFilterByFavorite } = props

  const [isDialogOpened, setIsDialogOpened] = useState(false)

  const { t } = useTranslation()

  const { isLoggedIn } = useAccountContext()

  const { mutate: createFavorite } = useCreateFavoriteFilter()

  const { mutate: removeFavorite } = useRemoveFavoriteFilter()

  const { data: favoriteFilters, fetch: fetchFavoriteFilters } =
    useGetFavoriteFilter<T>()

  const saveId = useMemo(() => {
    const noUndefedFilterInput = R.reject(R.isNil, filterInput)

    return favoriteFilters?.find(
      s =>
        equals(noUndefedFilterInput, R.reject(R.isNil, s.filterInput)) &&
        equals(sortInput, R.reject(R.isNil, s.sortInput))
    )?.id
  }, [favoriteFilters, filterInput, sortInput])

  const handleOnPromptAdd = useCallback((e: MouseEvent) => {
    e.stopPropagation()
    setIsDialogOpened(true)
  }, [])

  const handleOnAdd = useCallback(
    async (name: string) => {
      const data = await createFavorite({
        filterInput,
        name,
        sortInput,
        type
      })

      if (data) {
        fetchFavoriteFilters(type)
      }
    },
    [createFavorite, fetchFavoriteFilters, filterInput, sortInput, type]
  )

  const handleOnDelete = useCallback(
    async (id: string) => {
      const success = await removeFavorite({ type, id })

      if (success) {
        fetchFavoriteFilters(type)
      }
    },
    [fetchFavoriteFilters, removeFavorite, type]
  )

  const handleOnSelect = useCallback(
    (savedSearch: FavoriteFilter<T>) => {
      if (onFilterByFavorite) {
        onFilterByFavorite(savedSearch.filterInput, savedSearch.sortInput)
      }
    },
    [onFilterByFavorite]
  )

  useEffect(() => {
    if (isLoggedIn) {
      fetchFavoriteFilters(type)
    }
  }, [isLoggedIn])

  return (
    <FilterSection className={className} defaultExpanded>
      <Header>
        <FilterSectionTitle>
          <WithInfoContainer>
            {t('filter.sections.saved_search.title')}
            <Info tooltipId='favorite_filter'>
              {t('filter.sections.saved_search.info')}
            </Info>
          </WithInfoContainer>
        </FilterSectionTitle>
        <AddButton
          disableRipple
          disabled={saveId != null}
          onClick={handleOnPromptAdd}
        >
          <Icon src={AddSVG} />
        </AddButton>
      </Header>
      <Content>
        <ItemList>
          {favoriteFilters?.map(s => (
            <Item
              key={s.id}
              savedSearch={s}
              isCurrent={saveId === s.id}
              onSelect={handleOnSelect}
              onDelete={handleOnDelete}
            />
          )) ?? []}
        </ItemList>
      </Content>
      <SaveFilterDialog
        isOpened={isDialogOpened}
        setIsOpened={setIsDialogOpened}
        onAdd={handleOnAdd}
      />
    </FilterSection>
  )
}

export type SaveFilterProps<T extends FilterInput> = {
  className?: string
  filterInput: T
  sortInput: SortInputFromFilterInput<T>
  type: FavoriteType
  onFilterByFavorite?: (filter: T, sorting: SortInputFromFilterInput<T>) => void
}

export default SaveFilter
